import React, { useState, useEffect } from 'react';
import { Box, Text, VStack, HStack } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import BackButton from '../components/BackButton';
import PhotoGallery from '../components/PhotoGallery';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { getCityLabel, getCountryLabel, isSubscriptionExpired } from '../utils';
import { API_ROUTES, buildRoute, ROUTES, ADMIN_TABS, USER_TYPES } from '../constants';
import { getCategoryLabel } from '../shared-constants';

interface UserProfileDetailsPageProps {}

const UserProfileDetailsPage: React.FC<UserProfileDetailsPageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();
  const { user } = useUser();

  const [error, setError] = useState<string | null>(null);

  // Admin emails (fetched on mount to check if viewed user is an admin)
  const [adminEmails, setAdminEmails] = useState<Set<string>>(new Set());

  const isAdminMode = location.state?.isAdminMode || false;
  const userData = location.state?.userData || {};
  const fromTab = location.state?.fromTab || ADMIN_TABS.SEARCH;
  const userType: string = location.state?.userType || USER_TYPES.INFLUENCER;

  // Fetch full user details from backend
  const [fullUserData, setFullUserData] = useState<any>(userData);

  useEffect(() => {
    if (isAdminMode && !user) {
      const currentPath = window.location.pathname + window.location.search;
      if (!currentPath.includes('/login')) {
        sessionStorage.setItem('authReturnPath', currentPath);
      }
      navigate(ROUTES.LOGIN);
      return;
    }

    const fetchFullDetails = async () => {
      if (!isAdminMode || !config?.apiUrl || !userData.id) return;

      try {
        const response = await axios.get(`${config.apiUrl}${buildRoute(API_ROUTES.ADMIN_USER_BY_ID, { id: userData.id })}`, {
          params: {
            userType: userType
          }
        });
        setFullUserData(response.data);
      } catch (err: any) {
        console.error('Error fetching user details:', err);
        setError(err.response?.data?.error || 'Failed to load user details');
      }
    };

    if (isAdminMode && userData.id && !fullUserData.data) {
      fetchFullDetails();
    }
  }, [isAdminMode, userData.id, config?.apiUrl, user, fullUserData.data, navigate, userType]);

  // Fetch admin emails on mount
  useEffect(() => {
    if (!config?.apiUrl) return;

    const fetchAdminEmails = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}/api/admin/admin-emails`);
        if (response.data?.emails) {
          setAdminEmails(new Set(response.data.emails.map((e: string) => e.toLowerCase().trim())));
        }
      } catch (err) {
        console.error('Failed to fetch admin emails:', err);
      }
    };
    fetchAdminEmails();
  }, [config?.apiUrl]);

  // Check if the viewed user is an admin
  const isAdminUser = (email: string | undefined | null): boolean => {
    if (!email) return false;
    return adminEmails.has(email.toLowerCase().trim());
  };

  const handleBack = () => {
    navigate(ROUTES.ADMIN_USERS, {
      state: {
        returnToTab: fromTab
      }
    });
  };

  // Determine photos
  const photos = fullUserData.data?.photos?.length > 0
    ? fullUserData.data.photos
    : fullUserData.data?.image ? [fullUserData.data.image] : [];

  // Show error if no user data available
  if (!fullUserData) {
    return (
      <Box className="base-container" display="flex" alignItems="center" justifyContent="center" minH="100vh">
        <Text color="#F00C23">User data not available</Text>
      </Box>
    );
  }

  // Build display name
  const displayName = userType === USER_TYPES.INFLUENCER
    ? ([fullUserData.data?.firstName, fullUserData.data?.lastName].filter(Boolean).join(' ') || userData.name || 'Unknown')
    : (fullUserData.data?.businessName || [fullUserData.data?.contactPerson?.firstName, fullUserData.data?.contactPerson?.lastName].filter(Boolean).join(' ') || userData.name || 'Unknown');

  // Build location string
  const locationStr = fullUserData.city && fullUserData.country
    ? `${getCityLabel(fullUserData.city)}, ${getCountryLabel(fullUserData.country)}`
    : fullUserData.country
      ? getCountryLabel(fullUserData.country)
      : undefined;

  // Render influencer-specific fields
  const renderInfluencerFields = () => {
    return (
      <>
        {locationStr && (
          <Text className="static-text-big-white">
            Location - {locationStr}
          </Text>
        )}

        {fullUserData.data?.social?.instagram && (
          <Text className="static-text-big-white">
            Instagram - {fullUserData.data.social.instagram}
          </Text>
        )}

        {fullUserData.data?.social?.tiktok && (
          <Text className="static-text-big-white">
            Tiktok - {fullUserData.data.social.tiktok}
          </Text>
        )}

        {fullUserData.data?.social?.youtube && (
          <Text className="static-text-big-white">
            Youtube - {fullUserData.data.social.youtube}
          </Text>
        )}

        <Text className="static-text-big-white">
          Allergies - {fullUserData.data?.allergies || 'None'}
        </Text>
      </>
    );
  };

  // Render business-specific fields
  const renderBusinessFields = () => {
    const bd = fullUserData.data || {};
    const contact = bd.contactPerson || {};
    const social = bd.social || {};
    const categoryLabel = getCategoryLabel(fullUserData.businessCategory);
    return (
      <>
        {categoryLabel && (
          <Text className="static-text-big-white">
            Category - {categoryLabel}
          </Text>
        )}
        {bd.businessName && (
          <Text className="static-text-big-white">
            Business name - {bd.businessName}
          </Text>
        )}
        {locationStr && (
          <Text className="static-text-big-white">
            Location - {locationStr}
          </Text>
        )}
        {bd.businessAddress && (
          <Text className="static-text-big-white">
            Address - {bd.businessAddress}
          </Text>
        )}
        {contact.firstName && (
          <Text className="static-text-big-white">
            Contact first name - {contact.firstName}
          </Text>
        )}
        {contact.lastName && (
          <Text className="static-text-big-white">
            Contact last name - {contact.lastName}
          </Text>
        )}
        {contact.position && (
          <Text className="static-text-big-white">
            Position - {contact.position}
          </Text>
        )}
        {contact.phoneNumber && (
          <Text className="static-text-big-white">
            Phone - {contact.phoneNumber}
          </Text>
        )}
        {social.instagram && (
          <Text className="static-text-big-white">
            Instagram - {social.instagram}
          </Text>
        )}
        {social.tiktok && (
          <Text className="static-text-big-white">
            Tiktok - {social.tiktok}
          </Text>
        )}
        {social.website && (
          <Text className="static-text-big-white">
            Website - {social.website}
          </Text>
        )}
      </>
    );
  };

  // Subscription status string (read-only)
  const paidTillDate = fullUserData.data?.paidTillDate;
  const subscriptionExpired = isSubscriptionExpired(paidTillDate);
  const subscriptionLabel = !paidTillDate
    ? 'No active subscription'
    : subscriptionExpired
      ? 'Subscription expired'
      : `Subscription valid until ${new Date(paidTillDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;

  return (
    <Box className="base-container">
      <PhotoGallery images={photos} />

      {/* Back Button */}
      <Box className="back-buttom-box">
        <BackButton
          onClick={handleBack}
          borderColor="#C6D959"
          backgroundColor="rgba(198, 217, 89, 0.1)"
        />
      </Box>

      {/* Profile Name */}
      <Box
        position="absolute"
        top="320px"
        left="24px"
        zIndex={10}
      >
        <Text className="title-text-big">
          {displayName}
        </Text>
      </Box>

      {/* Divider */}
      <Box
        className="navigation-bar-divider"
        top="370px"
      />

      {/* Profile Fields */}
      <Box
        position="absolute"
        top="400px"
        left="24px"
        right="24px"
        zIndex={10}
        maxHeight="calc(100vh - 500px)"
        overflowY="auto"
        sx={{
          '&::-webkit-scrollbar': { display: 'none' },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none'
        }}
      >
        <VStack spacing="10px" align="start">
          {userType === USER_TYPES.INFLUENCER ? renderInfluencerFields() : renderBusinessFields()}
          {/* Subscription status — read-only, hidden for admin users */}
          {!isAdminUser(userData.email) && (
            <HStack spacing="8px" marginTop="8px">
              <Box
                width="8px"
                height="8px"
                borderRadius="50%"
                backgroundColor={subscriptionExpired ? '#595C5D' : '#C6D959'}
              />
              <Text className="subtitle-text" color="#90909A">
                {subscriptionLabel}
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>

      {/* Error Message */}
      {error && (
        <Box
          position="absolute"
          bottom="100px"
          left="24px"
          right="24px"
          zIndex={10}
          textAlign="center"
        >
          <Text className="static-text" color="#F00C23">
            {error}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default UserProfileDetailsPage;
